import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Save, Edit3, Clock, Target, Utensils, Loader2, GripVertical } from "lucide-react";
import { showErrorToast, showSuccessToast, showWarningToast } from "@/utils/toast-utils";
import { getMealPrograms, createMealProgram, updateMealProgram } from "@/lib/apiService";
import { CreateMealProgramRequest, UpdateMealProgramRequest, MealProgramMeal } from "@/types/meal-plan";
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Client } from '@/types/client';
import { MealKcalComparisonStatus } from './MealKcalComparisonStatus';
import { validateMealDistributionAgainstDCT } from '@/utils/meal-dct-validation';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { normalizeMealTypeForSelect } from '@/utils/meal-type-utils';

export interface MealTypeConfig {
  id: string;
  name: string;
  timeSlot: string;
  targetKcal?: number | null;
  mealType: 'breakfast' | 'lunch' | 'dinner' | 'snack' | 'dessert' | 'other';
  isDefault: boolean;
}

interface MealTypesIntegratedProps {
  clientId: string;
  clientName?: string;
  client?: Client;
  className?: string;
  onAddMeal?: () => void;
  onMealSlotAdded?: () => void;
  onMealSlotRemoved?: () => void;
  hideSaveButtons?: boolean;
  compactView?: boolean;
  showSingleMealSlotGuidance?: boolean;
  maxMealSlots?: number;
}

export interface MealTypesIntegratedRef {
  getCurrentMealTypes: () => MealTypeConfig[];
}

// Zod validation schema
const mealTypesSchema = z.object({
  mealTypes: z.array(
    z.object({
      id: z.string(),
      name: z.string().min(1, 'Meal name is required'),
      timeSlot: z.string().min(1, 'Time slot is required'),
      targetKcal: z.number().min(1, 'Target calories must be greater than 0').nullable().optional(),
      mealType: z.enum(['breakfast', 'lunch', 'dinner', 'snack', 'dessert', 'other']),
      isDefault: z.boolean(),
    })
  )
  // Validation enforced in handleSave: Either all meals have targetKcal or none do (no mixing)
});

type MealTypesFormData = z.infer<typeof mealTypesSchema>;

// Sortable Meal Type Item Component
interface SortableMealTypeProps {
  id: string;
  index: number;
  field: any;
  form: any;
  isEditing: boolean;
  onRemove: (index: number) => void;
}

const SortableMealType: React.FC<SortableMealTypeProps> = ({
  id,
  index,
  field,
  form,
  isEditing,
  onRemove
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-lg p-4 space-y-3 bg-background ${
        isDragging ? 'shadow-lg z-10' : ''
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div
            {...attributes}
            {...listeners}
            className={`cursor-grab active:cursor-grabbing p-1 rounded hover:bg-muted/50 transition-colors ${
              !isEditing ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            style={{ touchAction: 'none' }}
          >
            <GripVertical className="w-4 h-4 text-muted-foreground" />
          </div>
          <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">
            {index + 1}
          </span>
          <span className="text-sm font-medium text-muted-foreground">
            Meal Type #{index + 1}
          </span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onRemove(index)}
          className="text-destructive hover:bg-destructive/10"
          disabled={!isEditing}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`mealTypes.${index}.name`}>Meal Name</Label>
          <Controller
            name={`mealTypes.${index}.name`}
            control={form.control}
            render={({ field, fieldState }) => (
              <>
                <Input
                  {...field}
                  placeholder="e.g., Breakfast, Mid-morning Snack"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  disabled={!isEditing}
                />
                {fieldState.error && (
                  <p className="text-xs text-destructive">{fieldState.error.message}</p>
                )}
              </>
            )}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor={`mealTypes.${index}.mealType`}>Meal Type</Label>
          <Controller
            name={`mealTypes.${index}.mealType`}
            control={form.control}
            render={({ field, fieldState }) => (
              <>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={!isEditing}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select meal type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="breakfast">Breakfast</SelectItem>
                    <SelectItem value="lunch">Lunch</SelectItem>
                    <SelectItem value="dinner">Dinner</SelectItem>
                    <SelectItem value="snack">Snack</SelectItem>
                    <SelectItem value="dessert">Dessert</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {fieldState.error && (
                  <p className="text-xs text-destructive">{fieldState.error.message}</p>
                )}
              </>
            )}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor={`mealTypes.${index}.timeSlot`}>Time</Label>
          <Controller
            name={`mealTypes.${index}.timeSlot`}
            control={form.control}
            render={({ field, fieldState }) => (
              <>
                <Input
                  {...field}
                  type="time"
                  disabled={!isEditing}
                />
                {fieldState.error && (
                  <p className="text-xs text-destructive">{fieldState.error.message}</p>
                )}
              </>
            )}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor={`mealTypes.${index}.targetKcal`}>Target Kcal (Optional)</Label>
          <Controller
            name={`mealTypes.${index}.targetKcal`}
            control={form.control}
            render={({ field, fieldState }) => (
              <>
                <Input
                  type="number"
                  min="1"
                  value={field.value !== null && field.value !== undefined ? field.value : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    const numValue = parseInt(value);
                    // Convert empty, 0, or invalid to null
                    if (value === '' || isNaN(numValue) || numValue <= 0) {
                      field.onChange(null);
                    } else {
                      field.onChange(numValue);
                    }
                  }}
                  placeholder="e.g., 300 (optional)"
                  disabled={!isEditing}
                />
                {fieldState.error && (
                  <p className="text-xs text-destructive">{fieldState.error.message}</p>
                )}
              </>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export const MealTypesIntegrated = forwardRef<MealTypesIntegratedRef, MealTypesIntegratedProps>(({
  clientId,
  clientName,
  client,
  className,
  onAddMeal,
  onMealSlotAdded,
  onMealSlotRemoved,
  hideSaveButtons = false,
  compactView = false,
  showSingleMealSlotGuidance = false,
  maxMealSlots,
}, ref) => {
  const [mealTypes, setMealTypes] = useState<MealTypeConfig[]>([]);
  const [isEditing, setIsEditing] = useState(hideSaveButtons || compactView);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasReordered, setHasReordered] = useState(false);
  const [existingMealProgramId, setExistingMealProgramId] = useState<string | null>(null);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch meal types from API
  const fetchMealTypes = async () => {
    setIsLoading(true);
    try {
      const response = await getMealPrograms(clientId);
      if (response.success && response.data) {
        // New API structure: response.data is a single object with id and meals array
        const mealProgram = response.data;
        
        // Store meal program ID for updates
        if (mealProgram.id) {
          setExistingMealProgramId(mealProgram.id);
        }
        
        // Convert API meals to local meal types format
        const allMealTypes: MealTypeConfig[] = [];
        
        if (mealProgram.meals && Array.isArray(mealProgram.meals)) {
          mealProgram.meals.forEach((meal: any) => {
            allMealTypes.push({
              id: meal.id || `${mealProgram.id}-${meal.mealOrder}`,
              name: meal.mealName,
              timeSlot: meal.mealTime,
              targetKcal: meal.targetCalories && meal.targetCalories > 0 ? meal.targetCalories : null,
              mealType: normalizeMealTypeForSelect(meal.mealType),
              isDefault: false
            });
          });
        }
        
        setMealTypes(allMealTypes);
      } else {
        // If no API data, reset meal program ID and set empty meal types
        setExistingMealProgramId(null);
        setMealTypes([]);
      }
    } catch (error: any) {
      console.error('Error fetching meal types:', error);
      // On error, reset to empty state
      setExistingMealProgramId(null);
      setMealTypes([]);
      
      // Use backend response (if available) for a more specific message
      showErrorToast(error, "Failed to load meal types");
    } finally {
      setIsLoading(false);
    }
  };

  // Load meal types on component mount
  useEffect(() => {
    if (clientId) {
      fetchMealTypes();
    }
  }, [clientId]);

  useEffect(() => {
    if (hideSaveButtons || compactView) {
      setIsEditing(true);
    }
  }, [hideSaveButtons, compactView]);

  const createEmptyMealType = (): MealTypeConfig => {
    return {
      id: `meal-${Date.now()}`,
      name: '',
      timeSlot: '',
      targetKcal: null,
      mealType: 'breakfast',
      isDefault: false
    };
  };


  // React Hook Form setup
  const form = useForm<MealTypesFormData>({
    resolver: zodResolver(mealTypesSchema),
    defaultValues: {
      mealTypes: mealTypes
    }
  });

  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: "mealTypes"
  });

  // Handle drag end event
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = fields.findIndex((field) => field.id === active.id);
      const newIndex = fields.findIndex((field) => field.id === over?.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        move(oldIndex, newIndex);
        setIsEditing(true);
        setHasReordered(true);
      }
    }
  };

  // Update form when mealTypes state changes (only on initial load or API fetch)
  React.useEffect(() => {
    // Only reset if the form doesn't have any current values or if we're loading from API
    if (!form.formState.isDirty || isLoading) {
      form.reset({ mealTypes });
    }
  }, [mealTypes, form, isLoading]);

  const handleSave = async (values: MealTypesFormData) => {
    setIsSaving(true);
    try {
      const mealsWithKcal = values.mealTypes.filter(meal => {
        const value = meal.targetKcal;
        return value !== null && value !== undefined && typeof value === 'number' && value > 0;
      });
      
      const mealsWithoutKcal = values.mealTypes.filter(meal => {
        const value = meal.targetKcal;
        return value === null || value === undefined || value === 0 || value <= 0;
      });
      
      // Check if there's a mix (some have values, some don't)
      if (mealsWithKcal.length > 0 && mealsWithoutKcal.length > 0) {
        showWarningToast("Validation Error", "Either all meal slots should have target calories or none of them should. Please provide values for all meals or leave all empty.");
        setIsSaving(false);
        return;
      }
      
      // Validate DCT compliance before saving (only if meals have kcal values)
      const dctTarget = client?.clientGoal?.eerGoalCalories;
      const mealTypesForValidation = values.mealTypes.map(meal => ({
        id: meal.id || `meal-${Date.now()}`,
        name: meal.name || '',
        timeSlot: meal.timeSlot || '',
        targetKcal: meal.targetKcal,
        mealType: meal.mealType || 'breakfast' as const,
        isDefault: meal.isDefault || false
      }));
      
      const validationResult = validateMealDistributionAgainstDCT(mealTypesForValidation, dctTarget);
      
      // Only validate DCT if meals have kcal values (not the Edamam scenario)
      const hasKcalValues = mealsWithKcal.length > 0;
      let shouldOverrideCalorieMismatch = false;
      
      if (hasKcalValues && !validationResult.canProceed && validationResult.status !== 'no_dct') {
        const confirmed = window.confirm(
          `Warning: ${validationResult.message} This is outside the ±10% tolerance range. Do you want to proceed anyway?`
        );
        
        if (!confirmed) {
          setIsSaving(false);
          return;
        }
        shouldOverrideCalorieMismatch = true;
      }

      // Helper function to check if two meals are the same
      const areMealsEqual = (meal1: any, meal2: any): boolean => {
        return (
          meal1.mealName === meal2.mealName &&
          meal1.mealTime === meal2.mealTime &&
          meal1.mealType === meal2.mealType &&
          meal1.targetCalories === meal2.targetCalories &&
          meal1.mealOrder === meal2.mealOrder
        );
      };

      // Get current meals from API to compare
      let existingMeals: any[] = [];
      if (existingMealProgramId) {
        try {
          const currentResponse = await getMealPrograms(clientId);
          if (currentResponse.success && currentResponse.data?.meals) {
            existingMeals = currentResponse.data.meals;
          }
        } catch (error) {
          console.error('Error fetching current meals for comparison:', error);
        }
      }

      // Convert meal types to API format
      const mealsForAPI = values.mealTypes.map((meal, index): any => {
        const apiMeal: any = {
          mealOrder: index + 1,
          mealName: meal.name,
          mealTime: meal.timeSlot,
          mealType: meal.mealType,
        };
        if (meal.targetKcal !== null && meal.targetKcal !== undefined && meal.targetKcal > 0) {
          apiMeal.targetCalories = meal.targetKcal;
        }
        
        // Include ID if this meal already exists (for update endpoint)
        if (existingMealProgramId) {
          // Try to find matching existing meal by order or ID
          const existingMeal = existingMeals.find((em: any) => 
            em.mealOrder === index + 1 || em.id === meal.id?.replace(`${existingMealProgramId}-`, '')
          );
          if (existingMeal && existingMeal.id) {
            apiMeal.id = existingMeal.id;
          }
        }
        
        return apiMeal;
      });

      let response;
      
      // Use updateMealProgram if we have existing meal program, otherwise create new one
      if (existingMealProgramId) {
        // Check if meals have actually changed
        const mealsChanged = existingMeals.length !== mealsForAPI.length ||
          mealsForAPI.some((newMeal, index) => {
            const existingMeal = existingMeals[index];
            return !existingMeal || !areMealsEqual(newMeal, existingMeal);
          });

        if (!mealsChanged) {
          // No changes detected, skip API call
          showSuccessToast("Info", "No changes detected. Meal programme is already up to date.");
          setIsSaving(false);
          setIsEditing(false);
          setHasReordered(false);
          return;
        }

        // Update existing meal program (only send changed meals)
        const updateData: UpdateMealProgramRequest = {
          meals: mealsForAPI,
          ...(shouldOverrideCalorieMismatch ? { overrideCalorieMismatch: true } : {}),
        };
        response = await updateMealProgram(clientId, existingMealProgramId, updateData);
      } else {
        // Create new meal program
        const createData: CreateMealProgramRequest = {
          meals: mealsForAPI,
          ...(shouldOverrideCalorieMismatch ? { overrideCalorieMismatch: true } : {}),
        };
        response = await createMealProgram(clientId, createData);
      }

      if (response.success) {
        // Update local state with saved values
        setMealTypes(values.mealTypes as MealTypeConfig[]);
        setIsEditing(false);
        setHasReordered(false);
        
        // Call onAddMeal if provided (for integration with parent component)
        if (onAddMeal) {
          onAddMeal();
        }
        
        const action = existingMealProgramId ? 'updated' : 'created';
        showSuccessToast("Success", `Meal types ${action} successfully for ${clientName || 'client'}`);

        // Refresh the meal types from API to get the latest data
        await fetchMealTypes();
      } else {
        throw new Error(response.message || 'Failed to save meal types');
      }
    } catch (error: any) {
      console.error('Error saving meal types:', error);
      showErrorToast(error, "Failed to save meal types");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Revert to original API data by refetching
    fetchMealTypes();
    setIsEditing(false);
    setHasReordered(false);
  };

  const isAtMealSlotLimit = maxMealSlots != null && fields.length >= maxMealSlots;

  const handleAddMealType = () => {
    if (isAtMealSlotLimit) {
      showWarningToast(
        'Maximum reached',
        `You can add up to ${maxMealSlots} meal slots.`,
      );
      return;
    }

    append(createEmptyMealType());
    setIsEditing(true);
    onMealSlotAdded?.();
  };

  const handleRemoveMealType = (index: number) => {
    remove(index);
    onMealSlotRemoved?.();
  };

  // Implement the imperative handle functions
  useImperativeHandle(ref, () => ({
    getCurrentMealTypes: () => {
      // Return current form values if editing, otherwise return state
      if (isEditing) {
        const formValues = form.getValues();
        return (formValues.mealTypes || []).map(meal => ({
          id: meal.id || `meal-${Date.now()}`,
          name: meal.name || '',
          timeSlot: meal.timeSlot || '',
          targetKcal: meal.targetKcal ?? null,
          mealType: meal.mealType || 'breakfast' as const,
          isDefault: meal.isDefault || false
        }));
      }
      return mealTypes;
    }
  }), [isEditing, form, mealTypes]);

  return (
    <Card className={className}>
      <form onSubmit={form.handleSubmit(handleSave)}>
        <CardHeader className={compactView ? 'pb-3' : undefined}>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg font-semibold">
                <Utensils className="h-5 w-5 text-primary" />
                {compactView ? 'Meal Slots' : 'Meal Types Configuration'}
              </CardTitle>
              <p className="mt-1 text-sm text-muted-foreground">
                {compactView
                  ? maxMealSlots != null
                    ? `Add meal names, meal types, times, and optional target calories (up to ${maxMealSlots} slots).`
                    : 'Add meal names, meal types, times, and optional target calories.'
                  : maxMealSlots != null
                    ? `Create and manage up to ${maxMealSlots} meal types for this client. ${
                        fields.length > 1 && isEditing ? 'Drag the grip handle to reorder meal types.' : ''
                      }`
                    : `Create and manage meal types for this client. ${
                        fields.length > 1 && isEditing ? 'Drag the grip handle to reorder meal types.' : ''
                      }`}
              </p>
            </div>

            {!compactView && (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={fetchMealTypes}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Clock className="mr-2 h-4 w-4" />
                  )}
                  Refresh
                </Button>
                {!isEditing && fields.length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                  >
                    <Edit3 className="mr-2 h-4 w-4" />
                    Edit
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className={compactView ? 'space-y-4' : 'space-y-6'}>
          {!compactView && fields.length > 0 && (
            <MealKcalComparisonStatus
              mealTypes={form.watch('mealTypes').map(meal => ({
                id: meal.id || `meal-${Date.now()}`,
                name: meal.name || '',
                timeSlot: meal.timeSlot || '',
                targetKcal: meal.targetKcal ?? null,
                mealType: meal.mealType || 'breakfast' as const,
                isDefault: meal.isDefault || false
              }))}
              dctTarget={client?.clientGoal?.eerGoalCalories}
            />
          )}

          {fields.length > 0 && (
            <div
              className={
                compactView
                  ? 'rounded-lg border border-amber-200 bg-amber-50 p-3'
                  : 'rounded-lg border border-blue-200 bg-blue-50 p-3'
              }
            >
              <div className="flex items-start gap-2">
                <Target
                  className={`mt-0.5 h-4 w-4 flex-shrink-0 ${
                    compactView ? 'text-amber-700' : 'text-blue-600'
                  }`}
                />
                <div className={`space-y-1 text-sm ${compactView ? 'text-amber-800' : 'text-blue-800'}`}>
                  <p>
                    <span className="font-medium">Target Calories Rule:</span> Either all meal slots should have
                    target calories or none of them should. Mixing is not allowed.
                  </p>
                  {!compactView && (
                    <p>
                      <span className="font-medium">Important:</span> Ensuring daily calorie goals add up to the sum
                      of meal slot target calories is the nutritionist&apos;s onus. During meal plan generation,
                      priority is given to individual meal slot targets.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {showSingleMealSlotGuidance && fields.length === 1 && (
            <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-3">
              <div className="flex items-start gap-2">
                <Target className="mt-0.5 h-4 w-4 flex-shrink-0 text-cyan-700" />
                <p className="text-sm text-cyan-800">
                  When only one meal slot is added, all daily calories will be assigned to that single meal. Alternatively, you can add a kcal value to each meal slot to generate meals within those calorie limits.
                </p>
              </div>
            </div>
          )}

          {!compactView && fields.length > 0 && (
            <div className="grid grid-cols-1 gap-4 rounded-lg bg-muted/30 p-4 md:grid-cols-2">
              <div className="flex items-center gap-2">
                <Utensils className="h-4 w-4 text-primary" />
                <div>
                  <p className="text-sm font-medium">{fields.length} Meal Types</p>
                  <p className="text-xs text-muted-foreground">Total configured</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Target className="h-4 w-4 text-primary" />
                <div>
                  <p className="text-sm font-medium">
                    {form.watch('mealTypes').reduce((sum, meal) => sum + (meal.targetKcal || 0), 0)} kcal
                  </p>
                  <p className="text-xs text-muted-foreground">Daily goals</p>
                </div>
              </div>
            </div>
          )}

          {form.formState.errors.mealTypes?.message && (
            <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-4">
              <div className="flex items-start gap-3">
                <Target className="mt-0.5 h-5 w-5 text-destructive" />
                <div>
                  <h4 className="mb-1 font-medium text-destructive">Validation Error</h4>
                  <p className="text-sm text-destructive/80">
                    {form.formState.errors.mealTypes.message}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            {isLoading ? (
              <div className="py-8 text-center text-muted-foreground">
                <Loader2 className="mx-auto mb-4 h-8 w-8 animate-spin" />
                <p>Loading meal types...</p>
              </div>
            ) : compactView ? (
              fields.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-slate-200">
                  <div className="min-w-[900px]">
                    <div className="grid grid-cols-[52px_2fr_1.4fr_1.3fr_130px_48px] border-b bg-slate-50 px-2 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <span>#</span>
                      <span>Meal Name</span>
                      <span>Meal Type</span>
                      <span>Time</span>
                      <span>Target Kcal</span>
                      <span />
                    </div>

                    {fields.map((mealField, index) => (
                      <div
                        key={mealField.id}
                        className="grid grid-cols-[52px_2fr_1.4fr_1.3fr_130px_48px] items-center gap-2 border-b border-slate-100 px-2 py-2 last:border-b-0"
                      >
                        <span className="px-1 text-sm text-slate-500">{index + 1}</span>

                        <Controller
                          name={`mealTypes.${index}.name`}
                          control={form.control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              placeholder="Meal name"
                              autoComplete="off"
                              autoCorrect="off"
                              autoCapitalize="none"
                              spellCheck={false}
                              disabled={!isEditing}
                              className="h-9 min-w-0 border-slate-200"
                            />
                          )}
                        />

                        <Controller
                          name={`mealTypes.${index}.mealType`}
                          control={form.control}
                          render={({ field }) => (
                            <Select
                              value={field.value}
                              onValueChange={field.onChange}
                              disabled={!isEditing}
                            >
                              <SelectTrigger className="h-9 border-slate-200">
                                <SelectValue placeholder="Select meal type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="breakfast">Breakfast</SelectItem>
                                <SelectItem value="lunch">Lunch</SelectItem>
                                <SelectItem value="dinner">Dinner</SelectItem>
                                <SelectItem value="snack">Snack</SelectItem>
                                <SelectItem value="dessert">Dessert</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        />

                        <Controller
                          name={`mealTypes.${index}.timeSlot`}
                          control={form.control}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="time"
                              disabled={!isEditing}
                              className="h-9 border-slate-200"
                            />
                          )}
                        />

                        <Controller
                          name={`mealTypes.${index}.targetKcal`}
                          control={form.control}
                          render={({ field }) => (
                            <Input
                              type="number"
                              min="1"
                              value={field.value !== null && field.value !== undefined ? field.value : ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                const numValue = parseInt(value);
                                if (value === '' || isNaN(numValue) || numValue <= 0) {
                                  field.onChange(null);
                                } else {
                                  field.onChange(numValue);
                                }
                              }}
                              placeholder="Optional"
                              disabled={!isEditing}
                              className="h-9 border-slate-200"
                            />
                          )}
                        />

                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => remove(index)}
                          className="text-destructive hover:bg-destructive/10"
                          disabled={!isEditing}
                          title="Delete meal slot"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={fields.map(field => field.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {fields.map((field, index) => (
                    <SortableMealType
                      key={field.id}
                      id={field.id}
                      index={index}
                      field={field}
                      form={form}
                      isEditing={isEditing}
                      onRemove={handleRemoveMealType}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}

            {!isLoading && fields.length === 0 && (
              <div className="rounded-lg border-2 border-dashed py-12 text-center text-muted-foreground">
                <Utensils className="mx-auto mb-4 h-12 w-12 opacity-50" />
                <h3 className="mb-2 text-lg font-medium">
                  {compactView ? 'No meal slots added yet' : 'No meal types configured'}
                </h3>
                <p className="mb-4 text-sm">
                  {compactView
                    ? 'Start by adding the first meal slot.'
                    : 'Start by adding your first meal type to create a personalized meal plan.'}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddMealType}
                  disabled={isAtMealSlotLimit}
                  data-analytics-ignore={onMealSlotAdded ? 'true' : undefined}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {compactView ? 'Add Meal Slot' : 'Add Meal Type'}
                </Button>
              </div>
            )}
          </div>

          {!isLoading && fields.length > 0 && !isAtMealSlotLimit && (
            <div className={compactView ? 'pt-1' : 'flex justify-end pt-2'}>
              <Button
                type="button"
                variant={compactView ? 'ghost' : 'outline'}
                onClick={handleAddMealType}
                className={compactView ? 'h-auto px-0 text-cyan-700 hover:bg-transparent hover:text-cyan-800' : ''}
                data-analytics-ignore={onMealSlotAdded ? 'true' : undefined}
              >
                <Plus className="mr-2 h-4 w-4" />
                {compactView ? 'Add Meal Slot' : 'Add Meal Type'}
              </Button>
            </div>
          )}

          {isEditing && !hideSaveButtons && (
            <div className="flex justify-end gap-2 border-t pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSaving || (!form.formState.isValid && !hasReordered)}
              >
                {isSaving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          )}
        </CardContent>
      </form>
    </Card>
  );
});

MealTypesIntegrated.displayName = 'MealTypesIntegrated';
